import React, {useEffect, useState} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Radio,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import {useNavigate} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {LIST_GUIDES} from '../graphql/queries';
import {PUBLISH_GUIDE, UNPUBLISH_GUIDE, DELETE_GUIDE} from '../graphql/mutations';
import {useAuth} from '../contexts/AuthContext';
import {useOrg} from '../contexts/OrgContext';
import GuideListRow from '../components/GuideListRow';
import EntityReference from '../components/shared/EntityReference';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';

const TEMPLATES = [
  {
    id: 'application',
    title: 'Application process',
    description: 'Guide applicants through your standard application',
  },
  {
    id: 'enrollment',
    title: 'Enrollment verification',
    description: 'Verify enrollment eligibility and collect documents',
  },
  {
    id: 'records',
    title: 'Records request',
    description: 'Process requests for official records and transcripts',
  },
];

export default function GuidesPage() {
  useDocumentTitle('Guides');

  const {user} = useAuth();
  const {activeOrg} = useOrg();
  const navigate = useNavigate();

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const {loading, error, data, refetch} = useQuery(LIST_GUIDES, {
    skip: !user,
  });

  // Refetch guides when active org changes
  const activeOrgId = activeOrg?.id;
  useEffect(() => {
    if (activeOrgId && user) {
      refetch();
    }
  }, [activeOrgId, user, refetch]);

  const [publishGuide] = useMutation(PUBLISH_GUIDE, {
    refetchQueries: [{query: LIST_GUIDES}],
  });

  const [unpublishGuide] = useMutation(UNPUBLISH_GUIDE, {
    refetchQueries: [{query: LIST_GUIDES}],
  });

  const [deleteGuideMutation] = useMutation(DELETE_GUIDE, {
    refetchQueries: [{query: LIST_GUIDES}],
  });

  const handlePublish = async (guideId: string) => {
    await publishGuide({variables: {guideId}});
  };

  const handleUnpublish = async (guideId: string) => {
    await unpublishGuide({variables: {guideId}});
  };

  const handleDelete = async (guideId: string) => {
    await deleteGuideMutation({variables: {guideId}});
  };

  const guides = data?.listGuides || [];
  const hasGuides = !loading && !error && guides.length > 0;
  const isEmpty = !loading && !error && data?.listGuides && guides.length === 0;

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Header section */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Box>
          <Typography sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary}}>
            Guides
          </Typography>
          <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
            Public process landing pages for{' '}
            {activeOrg ? (
              <EntityReference kind="organization" label={activeOrg.name} orgSlug={activeOrg.slug} />
            ) : (
              'your organization'
            )}
          </Typography>
        </Box>
        <Box sx={{display: 'flex', gap: 1.5}}>
          <Button
            variant="contained"
            onClick={() => navigate('/org/guides/new')}
          >
            Create guide
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/org/guides/import-url')}
          >
            Import from URL
          </Button>
          <Button
            variant="outlined"
            onClick={() => setTemplateDialogOpen(true)}
          >
            Templates
          </Button>
          <Button
            variant="text"
            sx={{color: '#6B7280'}}
          >
            Docs
          </Button>
        </Box>
      </Box>

      {/* List card */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          overflow: 'hidden',
        }}
      >
        {/* Toolbar row */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <TextField
            size="small"
            placeholder="Search guides..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{fontSize: 20, color: '#9CA3AF'}} />
                </InputAdornment>
              ),
            }}
            sx={{width: 260}}
          />
          <Select
            size="small"
            defaultValue="all"
            disabled={guides.length === 0}
            sx={{minWidth: 140}}
          >
            <MenuItem value="all">All statuses</MenuItem>
          </Select>
        </Box>

        {/* Table header row */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#FAFAFA',
          }}
        >
          {[
            {label: 'Guide', flex: 1, width: undefined, align: 'left'},
            {label: 'Status', flex: undefined, width: 100, align: 'left'},
            {label: 'Last updated', flex: undefined, width: 140, align: 'left'},
            {label: 'Actions', flex: undefined, width: 120, align: 'right'},
          ].map(col => (
            <Typography
              key={col.label}
              sx={{
                flex: col.flex,
                width: col.width,
                fontSize: 12,
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: col.align as any,
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Body */}
        {loading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{m: 2}}>
            Error loading guides: {error.message}
          </Alert>
        )}

        {hasGuides && (
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            {guides.map((guide: any) => (
              <GuideListRow
                key={guide.id}
                guide={guide}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        )}

        {isEmpty && (
          <Box sx={{py: 6, px: 3, textAlign: 'center'}}>
            {/* Icon badge */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
              }}
            >
              <DescriptionIcon sx={{color: '#2563EB', fontSize: 24}} />
            </Box>

            {/* Headline */}
            <Typography sx={{fontSize: 18, fontWeight: 600, mt: 2}}>
              Create your first guide
            </Typography>

            {/* Body text */}
            <Typography
              sx={{
                fontSize: 14,
                color: '#6B7280',
                maxWidth: 420,
                mx: 'auto',
                mt: 1,
                lineHeight: 1.5,
              }}
            >
              Guides are public landing pages that explain a process and let applicants start a
              personalized journey.
            </Typography>

            {/* Actions */}
            <Box sx={{mt: 3, display: 'flex', justifyContent: 'center', gap: 1.5}}>
              <Button variant="contained" onClick={() => navigate('/org/guides/new')}>
                Create guide
              </Button>
              <Button variant="outlined" onClick={() => navigate('/org/guides/import-url')}>
                Import from URL
              </Button>
              <Button variant="outlined" onClick={() => setTemplateDialogOpen(true)}>
                Use a template
              </Button>
            </Box>

            {/* How it works row */}
            <Box
              sx={{
                mt: 4,
                bgcolor: '#F9FAFB',
                borderRadius: '8px',
                p: 2,
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
              }}
            >
              {[
                {num: '1', title: 'Draft', desc: 'Steps, requirements, deadlines'},
                {num: '2', title: 'Publish', desc: 'Get a shareable link'},
                {num: '3', title: 'Applicants start', desc: 'Creates a personalized plan'},
              ].map(step => (
                <Box key={step.num} sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: colors.blue[600],
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {step.num}
                  </Box>
                  <Box sx={{textAlign: 'left'}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600}}>{step.title}</Typography>
                    <Typography sx={{fontSize: 12, color: '#6B7280'}}>{step.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Template cards */}
            <Box
              sx={{
                mt: 4,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
              }}
            >
              {TEMPLATES.map(tpl => (
                <Box
                  key={tpl.id}
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    p: 2,
                    textAlign: 'left',
                  }}
                >
                  <Typography sx={{fontWeight: 600, fontSize: 14}}>{tpl.title}</Typography>
                  <Typography sx={{fontSize: 13, color: '#6B7280', mt: 0.5}}>
                    {tpl.description}
                  </Typography>
                  <Button size="small" variant="outlined" disabled sx={{mt: 1.5}}>
                    Use template
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Template picker dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Choose a template</DialogTitle>
        <DialogContent>
          {TEMPLATES.map(tpl => (
            <ListItemButton
              key={tpl.id}
              selected={selectedTemplate === tpl.id}
              onClick={() => setSelectedTemplate(tpl.id)}
              sx={{borderRadius: '8px', mb: 0.5}}
            >
              <Radio
                checked={selectedTemplate === tpl.id}
                size="small"
                sx={{mr: 1}}
              />
              <Box>
                <Typography sx={{fontWeight: 600, fontSize: 14}}>{tpl.title}</Typography>
                <Typography sx={{fontSize: 13, color: '#6B7280'}}>{tpl.description}</Typography>
              </Box>
            </ListItemButton>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled>
            Use template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
